import {
  _tasksExtraDataFilesSelectOneObjectByQuery,
  boardUserSelectByQuery,
  boardUserSelectOneFieldById,
  boardsSelectOneFieldById,
  boardViewSelectByQuery,
  boardViewSelectOneFieldByQuery,
  boardViewSelectOneObjectByQuery,
  boardsSelectors,
  ganttSelectByQuery,
  ganttSelectOneFieldByQuery,
  kanbanSelectOneFieldByQuery,
  kanbanSelectOneObjectByQuery,
  taskColumnOptionSelectByQuery,
  taskColumnOptionSelectOneObjectById,
  taskColumnSelectByQuery,
  taskColumnSelectors,
  taskGroupsSelectByQuery,
  taskGroupsSelectByQueryOnlyMaxMin,
  taskGroupsSelectOneFieldById,
  taskGroupsSelectOneFieldByQuery,
  taskGroupsSelectOneObjectByQuery,
  taskgroupsSelectors,
  tasksExtraDataSelectByQuery,
  tasksSelectByQuery,
  tasksSelectByQueryOnlyLength,
  tasksSelectOneFieldById,
  tasksSelectOneObjectById,
  tasksSelectors,
  viewFiltersSelectByQuery,
  workloadSelectOneFieldByQuery
} from "@common-reducers/DBServiceThunks";
import { createSelector } from "@reduxjs/toolkit";

import { Task as GanttTask } from "@common-components/reactgantt";
import {
  CompareFunctionOptions,
  TaskColumnType,
} from "@common-components/tasks/columns-cells/cell-interface";

import {
  myWorkStaticColumnList,
  staticColumnList,
} from "@common-components/tasks/helpers/columnList";
import { mergeGroupsAndTasks } from "@common-components/tasks/helpers/elements-factory/elementsFactory";
import { ContactInfo } from "@common-models/contact-info";
import { ExtraDataFile, ExtraDataItem } from "@common-models/ExtraDataItem";
import { Gantt } from "@common-models/gantt";
import { BoardRowType, CustomColumnData, Task } from "@common-models/task";
import { ColumnType, TaskColumn } from "@common-models/task-column";
import { TaskColumnOption } from "@common-models/task-column-option";
import { TasksGroup } from "@common-models/task-group";
import { User } from "@common-models/user";

import { columnTypeList, columnTypeMap } from "@common/components/tasks/columns-cells/column-cell-defs";
import { CommonRootState } from "@common/types/common-root-state-type";
import { NullableId } from "@common/types/interface";
import {
  AugmentedBlueticksBoardUser,
  getCurrentWorkspaceUserList,
  getMockPersonList,
  getSelectedBoardCompleteUserList,
  getSelectedBoardId
} from "./BoardsSelectors";
import { BatteryObject } from "./TaskInterfaces";

import { GenericState } from "./DBServiceReducersInterfaces";
import { isTaskMatchingFiltersSelector } from "./TasksFilterPanelThunks";
import { selectMergedContactList } from "./WhatsAppReducer";
import { debugLog } from "@common/utils/debug-logger";


export function sortTasksByCustomOrder(baseTasks: Task[], tasksOrder: Record<string, number>) {
  // Create a new array of tasks with updated order
  const newTasks = baseTasks.map((task) => ({
    ...task,
    order: tasksOrder[task._id ?? ''],
  }));

  // Sort the tasks based on the order
  newTasks.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return newTasks;
}


export const baseTasks = createSelector(
  // Pass only the necessary parts of the state to each selector.
  [
    getSelectedBoardId,
    state => state.DBTasksReducer,
    state => state.TasksReducer.tasksFilter,
    state => state.DBTaskgroupsReducer,
    state => getBoardView(state),
  ],
  (boardId, DBTasksReducer, tasksFilter, DBTaskgroupsReducer, boardView) => {
    // Now use the directly passed states or results from other selectors.
    let baseTasks = tasksSelectByQuery(
      DBTasksReducer,
      {
        boardId,
        ...tasksFilter,
        deleted: false,
        archived: { $ne: true },
        isTemplate: { $ne: false },
      },
      null,
      {
        order: 1,
      }
    );


    const boardGroups = taskGroupsSelectByQuery(DBTaskgroupsReducer, {
      boardId,
      $or: [{ deleted: { $exists: false } }, { deleted: false }],
    });

    const boardGroupIdSet = new Set(boardGroups.map((group) => group._id));
    baseTasks = baseTasks.filter(
      (task) => task.groupId && boardGroupIdSet.has(task.groupId)
    );

    if (boardView && boardView.tasksOrder) {
      baseTasks = sortTasksByCustomOrder(baseTasks, boardView.tasksOrder);
    }

    return baseTasks;
  }
);

export const selectBoardTaskCount = createSelector(
  [(state: CommonRootState) => state.DBTasksReducer, (_, boardId: string) => boardId],
  (DBTasksReducer, boardId) => {
    const taskList = tasksSelectByQuery(
      DBTasksReducer,
      {
        boardId,
        deleted: false,
        archived: { $ne: true },
        isTemplate: { $ne: false },
      },
      null,
      {
        order: 1,
      }
    );

    return taskList.length
  }
);

const personFilterSelector = (state: CommonRootState) => state.TasksReducer.personFilter;
const currentBoardViewSelector = (state: CommonRootState) => state.TasksReducer.currentBoardView;
const boardViewSelector = createSelector(
  [getSelectedBoardId, currentBoardViewSelector, (state: CommonRootState) => state.DBBoardViewReducer],
  (boardId, currentBoardView, reducer) => boardViewSelectOneObjectByQuery(reducer, { boardId: boardId, _id: currentBoardView.id })
);
const filterListSelector = createSelector(
  [boardViewSelector, (state: CommonRootState) => state.DBViewFiltersReducer, getSelectedBoardId],
  (boardView, reducer, boardId) => {
    let filterList = viewFiltersSelectByQuery(reducer, {
      viewId: boardView?._id ?? boardId,
      $or: [{ deleted: { $exists: false } }, { deleted: false }],
    }, null, { updatedAt: 1 });
    if (boardView?.filterView) {
      filterList = [...boardView.filterView];
    }
    return filterList;
  }
);

export const currentTasks = createSelector(
  [
    baseTasks,
    (state: CommonRootState) => personFilterSelector(state),
    (state: CommonRootState) => getSelectedBoardId(state),
    (state: CommonRootState) => currentBoardViewSelector(state).id,
    (state: CommonRootState) => columnListSelector(state, getSelectedBoardId(state)),
    (state: CommonRootState) => filterListSelector(state),
    (state: CommonRootState) => state.DBTaskColumnOptionReducer,
  ],
  (baseTasks, personFilter, boardId, currentBoardViewId, columnList, filterList, DBTaskColumnOptionReducer) => {
    let filteredTasks = baseTasks;
    let tasksFilteredList: Task[] = [];

    if (filterList.length > 0) {
      tasksFilteredList = filteredTasks.filter(task =>
        isTaskMatchingFiltersSelector(boardId, task, filterList, columnList, DBTaskColumnOptionReducer)
      );
    }

    if (personFilter !== "") {
      const personPickerColumns = columnList.filter(({ type }) => type === "person-picker");
      const personPickerColumnsIds = personPickerColumns.map(({ _id }) => _id).filter((id): id is string => id !== undefined);
      if (personPickerColumnsIds.length > 0) {
        filteredTasks = isTaskAssignedToOneOfThoseUsers(filteredTasks, personFilter, personPickerColumnsIds);
      }
    }

    if (filterList.length > 0) {
      filteredTasks = filteredTasks.filter(task => tasksFilteredList.includes(task));
    }

    return filteredTasks;
  }
);


export const compareValues = (a: unknown, b: unknown, options: CompareFunctionOptions) => {
  let compareVal;
  try {
    if (typeof a === "string" && typeof b === "string") {
      compareVal =
        options.sortOrder === "asc" ? a?.localeCompare(b) : b?.localeCompare(a);
    } else {
      const aNum = (a as number) || Infinity;
      const bNum = (b as number) || Infinity;
      compareVal = options.sortOrder === "asc" ? aNum - bNum : bNum - aNum;
    }
  } catch (e) {
    console.error(e);
  }

  return compareVal ?? 0;
};

export const sortTasksOrderByColumn = (
  taskList: Task[],
  column: TaskColumn,
  options: CompareFunctionOptions
): Task[] => {
  const columnType: TaskColumnType | undefined = columnTypeList.find(
    (columnType) => columnType.type === column.type
  );
  const compareFunction = columnType?.compareFunction ?? compareValues;

  taskList.sort((taskA, taskB) => {
    const valueA = cellValueByTaskAndColumnId(taskA, column);
    const valueB = cellValueByTaskAndColumnId(taskB, column);
    return compareFunction(valueA, valueB, options);
  });
  return taskList;
};

export const isTaskAssignedToOneOfThoseUsers = (
  tasks: Task[],
  personsToFilterBy: unknown,
  personPickerColumnsIds: string[]
) => {
  let filteredTasksByPerson: any[] = [];
  for (let i = 0; i < tasks.length; i++) {
    // console.log(`Task loop number ${i}`);
    let result: any[] = [];
    const currentTask = tasks[i];
    result = searchForPersonMatchInColumns(
      currentTask,
      personPickerColumnsIds,
      personsToFilterBy
    ) ?? [];
    if (result.length > 0) {
      filteredTasksByPerson.push(currentTask);
    }
  }
  return filteredTasksByPerson;
};

export const isTaskEqualToOptionColumn = (
  tasks: Task[],
  columnId: string,
  columnOptionId: string
) => { };

export const findDuplicates = (selectedOptionsCloned: Array<{ _id?: string }>) => {
  const duplicates: Record<string, number> = {};
  selectedOptionsCloned.forEach((obj) => {
    const key = obj?._id ?? '';
    if (!duplicates[key]) {
      duplicates[key] = 0;
    }
    duplicates[key]++;
  });
  return duplicates;
};

export const selectColumnOptions = (
  state: CommonRootState,
  { boardId, columnId, cellValue }: { boardId: string; columnId: string; cellValue: string }
) => {
  const columnOptions = taskColumnOptionSelectByQuery(
    state.DBTaskColumnOptionReducer,
    {
      boardId,
      columnId,
      $or: [{ deleted: { $exists: false } }, { deleted: false }],
    }
  );

  //return by cellvalue from columnoptions
  const columnOption = columnOptions.find((option) => option._id === cellValue);

  return columnOption;
};

export const searchForPersonMatchInColumns = (
  task: Task,
  currentColumn: string[],
  personsToFilterBy: unknown
) => {
  let tasksMatchedByPersonSelected: any[] = [];
  let personsSelectedUserIdList: any[] = [];

  //loop columnsList
  for (let i = 0; i < currentColumn.length; i++) {
    //console.log(`Column loop number ${i}`);
    const personsSelected = task?.customData?.[currentColumn[i]]?.value;
    if (personsSelected) {
      //loop column persons selected
      personsSelected.forEach(function (item: { id: string }) {
        personsSelectedUserIdList.push(item.id);
      });
    }
    const isPersonMatch = personsSelectedUserIdList
      ? personsSelectedUserIdList.includes(personsToFilterBy)
      : false;
    if (isPersonMatch) {
      tasksMatchedByPersonSelected.push(task);
      return tasksMatchedByPersonSelected;
    }
    if (i === currentColumn.length - 1) {
      return tasksMatchedByPersonSelected;
    }
  }
};

export const getColumnList = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState) => {
    let columnsData: (TaskColumn | undefined)[] = [];
    const currentBoardId = getSelectedBoardId(state);
    const taskColumnState: GenericState<TaskColumn> = state.DBTaskColumnReducer;
    const columnList = taskColumnState?.entities;
    columnsData = Object.values(columnList);
    const finalColumnList = columnsData.filter(
      (columnItem): columnItem is TaskColumn => columnItem !== undefined && columnItem.boardId === currentBoardId
    );
    return finalColumnList;
  }
);

export const boardTaskSelector = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState) => {
    return tasksSelectByQuery(state.DBTasksReducer, {
      boardId: getSelectedBoardId(state),
      $or: [{ deleted: { $exists: false } }, { deleted: false }],
    });
  }
);

export const createProjectItemByGroup = createSelector(
  [
    (state: CommonRootState) => state,
    (_, taskList: any[], columnId) => ({ taskList, columnId }),
  ],
  (state: CommonRootState, { taskList, columnId }) => {
    const listOfGroupTasks: any[][] = [];
    const listOfProjects: any[] = [];
    let groupId;
    let groupIdForBattery;
    for (let i = 0; i < taskList.length; i++) {
      const task = taskList[i];
      const taskGroupId = task?.groupId;
      const filterTasksByGroupID = taskList.filter((task) => {
        groupIdForBattery = task.groupId;
        return task.groupId === taskGroupId;
      });

      if (filterTasksByGroupID[0]?.groupId !== groupId) {
        groupId = filterTasksByGroupID[0]?.groupId;
        listOfGroupTasks.push(filterTasksByGroupID);
      }
    }

    const battaryGroup = getBattaryProgressGanttGroupBar(
      state,
      groupIdForBattery ?? ''
    );
    debugLog('task-selectors', battaryGroup);

    for (let i = 0; i < listOfGroupTasks.length; i++) {
      const projectTasks = listOfGroupTasks[i];
      let groupId, groupName, groupColor;

      const maxDate = new Date(
        Math.max(
          ...projectTasks.map((task: any) => {
            return new Date(task?.customData?.[columnId]?.value?.endDate).getTime();
          })
        )
      );
      const minDate = new Date(
        Math.min(
          ...projectTasks.map((task: any) => {
            return new Date(task?.customData?.[columnId]?.value?.startDate).getTime();
          })
        )
      );

      projectTasks.forEach((task) => {
        groupColor = task?.groupColor;
        groupId = task?.groupId;
        groupName = taskGroupsSelectOneFieldByQuery(
          state.DBTaskgroupsReducer,
          { _id: groupId },
          "name"
        );
      });

      const projectObj: any = {
        start: new Date(minDate),
        end: new Date(maxDate),
        name: groupName,
        id: groupId,
        progress: 100,
        isDisable: false,
        type: "project",
        hideChildren: false,
        styles: {
          projectBackgroundColor: groupColor,
          projectBackgroundCriticalColor: groupColor,
          projectBackgroundSelectedColor: groupColor,
          // groupProgressColor:'linear-gradient(90deg, rgba(37,154,233,1) 1%, rgba(0,212,255,0) 1%)',
          // groupProgressCriticalColor:'red',
          // projectProgressColor: "red",
          // projectProgressCriticalColor: "linear-gradient(90deg, rgba(37,154,233,1) 20%, rgba(0,212,255,0) 1%);",
          // projectProgressSelectedColor: "linear-gradient(90deg, rgba(37,154,233,1) 20%, rgba(0,212,255,0) 1%)",
          // projectProgressSelectedCriticalColor: "linear-gradient(90deg, rgba(37,154,233,1) 20%, rgba(0,212,255,0) 1%)",
        },
      };
      const thisProjectExist = listOfProjects.find(
        (task) => projectObj.id === task.id
      );
      if (!thisProjectExist) {
        listOfProjects.push(projectObj);
      }
    }

    return listOfProjects;
  }
);

export const getGanttTaskItem = createSelector(
  [
    (state: CommonRootState) => state,
    (_, taskId: any, columnId) => ({ taskId, columnId }),
  ],
  (state: CommonRootState, { taskId, columnId }) => {
    const ganttTaskList = currentTasks(state);

    for (let i = 0; i < ganttTaskList.length; i++) {
      const task = ganttTaskList[i];
      if (taskId === task._id) {
        return task.customData?.[columnId]?.value;
      }
    }
  }
);

export const getGanttColumnValueItem = createSelector(
  [
    (state: CommonRootState) => state,
    (_, picker: string, taskId: any) => ({ picker, taskId }),
  ],
  (state: CommonRootState, { picker, taskId }) => {
    const boardId = getSelectedBoardId(state);
    const finalList: any[] = [];
    const columnList = columnListSelector(state, boardId);
    const allColumns = columnList.filter((column) => column.type === picker);

    allColumns.forEach((column) => {
      if (column.type === picker) {
        const ganttTask: any = getGanttTaskItem(state, taskId, column?._id);
        if (ganttTask) {
          finalList.push(...ganttTask);
        }
      }
    });

    return finalList;
  }
);

export const getFixedDependencyList = createSelector(
  [
    (state: CommonRootState) => state,
    (_, ganttTaskList: any[]) => ({ ganttTaskList }),
  ],
  (state: CommonRootState, { ganttTaskList }) => {
    const currentBoardTasks = currentTasks(state);
    function addDays(date: Date, days: number) {
      date.setDate(date.getDate() + days);
      return date;
    }
    function minusDays(date: Date, days: number) {
      date.setDate(date.getDate() - days);
      return date;
    }
    for (let i = 0; i < ganttTaskList.length; i++) {
      const task = ganttTaskList[i];
      if (
        task?.type === "task" &&
        Object.keys(task?.dependencies[0] ?? {}).length !== 0
      ) {
        const arrowEndTarget = task?.dependencies[0]?.ownTarget;
        const arrowStartTarget = task?.dependencies[0]?.sourceTarget;
        const taskSendArrowId = task?.dependencies[0]?.sourceId;
        const taskSendArrow = ganttTaskList.find((task) => {
          if (taskSendArrowId === task?.id) return task;
        });

        switch (arrowEndTarget) {
          case "startOfTask":
            if (arrowStartTarget === "startOfTask") {
              if (taskSendArrow.start.getTime() > task?.start.getTime()) {
                const days =
                  (taskSendArrow.start.getTime() - task?.start.getTime()) /
                  (1000 * 3600 * 24);
                task.start = addDays(task.start, days);
                task.end = addDays(task.end, days);
              }
            } else if (arrowStartTarget === "endOfTask") {
              if (taskSendArrow.end.getTime() > task?.start.getTime()) {
                const days =
                  (taskSendArrow.end.getTime() - task?.start.getTime()) /
                  (1000 * 3600 * 24);
                task.start = addDays(task.start, days + 1);
                task.end = addDays(task.end, days + 1);
              }
            }
            break;
          case "endOfTask":
            if (arrowStartTarget === "startOfTask") {
              if (taskSendArrow.start.getTime() >= task?.end.getTime()) {
                const days =
                  (taskSendArrow.start.getTime() - task?.end.getTime()) /
                  (1000 * 3600 * 24);
                task.start = addDays(task.start, days + 1);
                task.end = addDays(task.end, days + 1);
              }
            } else if (arrowStartTarget === "endOfTask") {
              if (taskSendArrow.end.getTime() >= task?.end.getTime()) {
                const days =
                  (taskSendArrow.end.getTime() - task?.end.getTime()) /
                  (1000 * 3600 * 24);
                task.start = addDays(task.start, days + 1);
                task.end = addDays(task.end, days + 1);
              }
            }

            break;
        }
      }
    }
  }
);

export const getBattaryProgressGanttGroupBar = createSelector(
  [(state: CommonRootState) => state, (_, groupId: string) => ({ groupId })],
  (state: CommonRootState, { groupId }) => {
    const boardId = getSelectedBoardId(state);

    const type = "status-option-picker";
    const columnList = columnListSelector(state, boardId).map(
      (column, index) =>
      ({
        type: column.type,
        _id: column._id,
        title: column.title,
      } as TaskColumn)
    );
    const columnRes = columnList.filter(
      (columnItem) => columnItem.type === type
    );
    const columnId = columnRes[0]?._id;
    const params = {
      groupId,
      columnId,
      boardId,
      columnType: "status-option-picker",
    };
    const batteryIndicator: BatteryObject[] = [];
    const values = getColumnValues(state, params);
    let percentageForTask;

    const getPercentage = (optionCount: number, totalTasks: number) => {
      const percentage = (optionCount / totalTasks) * 100;
      return percentage % 1 !== 0 ? percentage.toFixed(2) : percentage;
    };
    type SelectedOption = { _id?: string; order?: number; bgcolor?: string; count?: number };
    const valuesAsOptionShape = values as { selectedOptionsCloned?: SelectedOption[]; tasksLength?: number };
    const selectedOptions = valuesAsOptionShape?.selectedOptionsCloned?.filter(
      (obj: SelectedOption, index: number, self: SelectedOption[]) => {
        return index === self.findIndex((t) => t?._id === obj?._id);
      }
    );
    const sortedSelectedOptions = selectedOptions?.sort(
      (a: SelectedOption, b: SelectedOption) => (a?.order ?? 0) - (b?.order ?? 0)
    );
    let res = `linear-gradient(`;

    sortedSelectedOptions?.forEach((column: SelectedOption, index: number) => {
      // console.log(column)
      const columnColor = column?.bgcolor;

      percentageForTask = getPercentage(column?.count ?? 0, valuesAsOptionShape?.tasksLength ?? 0);
      batteryIndicator.push({
        bgColor: (columnColor ?? '') as String,
        percentage: percentageForTask as Number,
      });
    });
    // console.log(batteryIndicator)
    batteryIndicator.map((groupOfStatus) => {
      const itemGradient = `90deg, ${groupOfStatus.bgColor}, ${groupOfStatus.percentage},`;
      res += itemGradient;
    });
    res += " )";
    // console.log(res)
    return res;
  }
);

export const getGanttTasks = createSelector(
  [(state: CommonRootState) => state, (_, columnId: NullableId) => columnId],
  (state: CommonRootState, columnId) => {
    const boardId = getSelectedBoardId(state);
    const tasksList = getTasksWithValueInColumnIdSelector(state, columnId);
    const projectList = createProjectItemByGroup(state, tasksList, columnId);
    const ganttsListInBoard = ganttSelectByQuery(state.DBGanttReducer, {
      boardId: boardId,
    });
    const orderTasksBy = ganttSelectOneFieldByQuery(
      state.DBGanttReducer,
      { _id: ganttsListInBoard[0]._id },
      "orderTasksBy"
    );
    const milestoneTasks = ganttSelectOneFieldByQuery(
      state.DBGanttReducer,
      { _id: ganttsListInBoard[0]._id },
      "milestoneTasks"
    );
    const groupView = ganttSelectOneFieldByQuery(
      state.DBGanttReducer,
      { _id: ganttsListInBoard[0]._id },
      "showTasksByGroup"
    );

    const fixDependency = ganttSelectOneFieldByQuery(
      state.DBGanttReducer,
      { _id: ganttsListInBoard[0]._id },
      "fixDependency"
    );

    // const defaultColors: ColorStyles = {
    //   arrowColor: "grey",
    //   arrowCriticalColor: "#ff0000",
    //   arrowWarningColor: "var(--color-text)",
    //   barProgressColor: "var(--color-text)",
    //   barProgressCriticalColor: "#ff1919",
    //   barProgressSelectedColor: "#8282f5",
    //   barProgressSelectedCriticalColor: "#ff0000",
    //   barBackgroundColor: "var(--color-text)",
    //   barBackgroundCriticalColor: "#ff6363",
    //   barBackgroundSelectedColor: "#aeb8c2",
    //   barBackgroundSelectedCriticalColor: "#ff8e8e",
    //   groupProgressColor: "#2dbb2e",
    //   groupProgressCriticalColor: "#2dbb2e",
    //   groupProgressSelectedColor: "#28a329",
    //   groupProgressSelectedCriticalColor: "#28a329",
    //   groupBackgroundColor: "#006bc1",
    //   groupBackgroundCriticalColor: "#006bc1",
    //   groupBackgroundSelectedColor: "#407fbf",
    //   groupBackgroundSelectedCriticalColor: "#407fbf",
    //   projectProgressColor: "#7db59a",
    //   projectProgressCriticalColor: "#7db59a",
    //   projectProgressSelectedColor: "#59a985",
    //   projectProgressSelectedCriticalColor: "#59a985",
    //   projectBackgroundColor: "#fac465",
    //   projectBackgroundCriticalColor: "#fac465",
    //   projectBackgroundSelectedColor: "#f7bb53",
    //   projectBackgroundSelectedCriticalColor: "#f7bb53",
    //   milestoneBackgroundColor: "#f1c453",
    //   milestoneBackgroundCriticalColor: "#ff8e8e",
    //   milestoneBackgroundSelectedColor: "#f29e4c",
    //   milestoneBackgroundSelectedCriticalColor: "#ff0000",
    //   evenTaskBackgroundColor: "#f5f5f5",
    //   holidayBackgroundColor: "rgba(233, 233, 233, 0.3)",
    //   selectedTaskBackgroundColor: "rgba(252, 248, 227, 0.5)",
    //   todayColor: "rgba(252, 248, 227, 0.5)",
    //   contextMenuBoxShadow: "rgb(0 0 0 / 25%) 1px 1px 5px 1px",
    //   contextMenuBgColor: "var(--color-surface)",
    //   contextMenuTextColor: "inherit",
    // };
    const completeGanttList: any[] = [];
    const groupTasks: TasksGroup[] = taskgroupsSelectors.selectAll(state);
    const ganttTasks: GanttTask[] = [];
    const currentDate = new Date();
    for (let i = 0; i < tasksList.length; i++) {
      const ganttTask = tasksList[i];

      // Get ID value
      const ganttTaskId = tasksList[i]?._id;
      // Get Dependencies
      const dependenciesInTask: any[] = [];
      const ganttDependency = ganttsListInBoard[0]?.tasksDependencies;
      const dependencyKeys = ganttDependency
        ? Object.keys(ganttDependency)
        : {};
      if (Object.keys(dependencyKeys).length !== 0 && ganttDependency && ganttTaskId !== undefined) {
        const addToDeps = (ganttDependency as Record<string, unknown>)[ganttTaskId];
        dependenciesInTask[0] = { ...(addToDeps as object) };
      }
      // GET Date value
      const cellValue = cellValueSelector(state, tasksList[i]?._id, columnId);
      let startDate = new Date(cellValue?.startDate) ?? new Date(currentDate);
      let endDate =
        new Date(cellValue?.endDate) ?? new Date(currentDate.getDate() + 1);

      // GET Progress value
      const progress = 0;
      // Get task name value
      const ganttTaskName = tasksList[i]?.text;
      // Get groupId value
      const groupId = groupTasks.find(
        (groupId) => groupId._id === ganttTask.groupId
      );
      // Get Color Value
      const groupColor = ganttTask?.groupColor;
      if (startDate >= endDate) {
        const helper = startDate;
        startDate = endDate;
        endDate = helper;
      }
      const x = (milestoneTasks as string[] | undefined)?.find((id) => ganttTaskId === id);

      const taskObj: GanttTask = {
        start: new Date(startDate),
        end: new Date(endDate),
        name: ganttTaskName ?? '',
        id: ganttTaskId ?? '',
        type: x ? "milestone" : "task",
        progress: progress,
        isDisabled: false,
        dependencies: dependenciesInTask,
        parent: groupId?._id,
        styles: {
          barBackgroundColor: groupColor,
          barBackgroundSelectedColor: groupColor,
        },
      };

      const thisTaskExist = ganttTasks.find((task) => taskObj.id === task.id);
      if (!thisTaskExist) {
        ganttTasks.push(taskObj);
      }
    }
    if (groupView) {
      completeGanttList.push(...ganttTasks, ...projectList);
    } else {
      completeGanttList.push(...ganttTasks);
    }
    let sortedGanttList = getSortedGanttTasksFromTasksOrder(
      state,
      completeGanttList,
      ganttsListInBoard[0]
    );

    if (fixDependency === true) {
      getFixedDependencyList(state, sortedGanttList);
    }
    if (orderTasksBy === "customOrder" || orderTasksBy === undefined) {
      sortedGanttList = getSortedGanttTasksFromTasksOrder(
        state,
        completeGanttList,
        ganttsListInBoard[0]
      );
    } else if (orderTasksBy === "startDate") {
      sortedGanttList = completeGanttList.sort(function (a: any, b: any) {
        return a.start - b.start;
      });
    } else if (orderTasksBy === "endDate") {
      sortedGanttList = completeGanttList.sort(function (a: any, b: any) {
        return b.end - a.end;
      });
    }

    return sortedGanttList;
  }
);

export const getTasksType = createSelector(
  [
    (state: CommonRootState) => state,
    (_state, taskId: NullableId, gantt: Gantt) => ({ taskId, gantt }),
  ],
  (state: CommonRootState, { taskId, gantt }) => {
    const milestoneTasks = gantt?.milestoneTasks;
    debugLog('task-selectors', milestoneTasks);

    if (milestoneTasks?.find((taskIdFromList) => taskId === taskIdFromList)) {
      return "milestone";
    } else {
      return "task";
    }
  }
);

export const getSortedGanttTasksFromTasksOrder = createSelector(
  [
    (state: CommonRootState) => state,
    (_state, ganttTaskList: any[], gantt: Gantt) => ({ ganttTaskList, gantt }),
  ],
  (state: CommonRootState, { ganttTaskList, gantt }) => {
    const ganttTasksOrderList = gantt?.tasksOrder;
    let sortedList: any[] = [];
    if ((ganttTasksOrderList?.length ?? 0) > 1 && ganttTasksOrderList) {
      for (const taskId in ganttTasksOrderList) {
        const taskOrderItemId = Object.keys(ganttTasksOrderList[taskId]);
        Object.values(ganttTasksOrderList[taskId]);
        const task = ganttTaskList.find(
          (task) => task.id === taskOrderItemId[0]
        );
        sortedList.push(task);
      }
      const listOfItemThatNotInTasksOrder = ganttTaskList.filter(
        (task) => !sortedList.includes(task)
      );
      sortedList.push(...listOfItemThatNotInTasksOrder);
    } else {
      sortedList = [...ganttTaskList];
    }
    return sortedList;
  }
);

export const currentGroups = createSelector(
  [
    (state: CommonRootState) => state.DBTaskgroupsReducer,
    getSelectedBoardId,
  ],
  (DBTaskgroupsReducer, selectedBoardId) => {
    return taskGroupsSelectByQuery(
      DBTaskgroupsReducer,
      {
        boardId: selectedBoardId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
        archived: { $ne: true },
      },
      [
        "_id",
        "isCollapsed",
        "colorAccent",
        "order",
        "isFooterAutoFocused",
        "isSubFooterAutoFocused",
      ],
      {
        order: 1,
      }
    );
  }
);

export const selectElementsList = createSelector(
  [
    currentTasks,
    currentGroups,
    getSelectedBoardId,
    (state: CommonRootState) => state.TasksReducer.isGroupDragging,
    (state: CommonRootState) => state.TasksReducer.toggleTasksMap,
    (state: CommonRootState) => state.TasksReducer.tasksFilter.text
  ],
  (currentTasks, currentGroups, selectedBoardId, isGroupDragging, toggleTasksMap, textFilter) => {
    let hideEmptyGroups = false;

    
    // Enable hiding empty groups even when there is a text search
    // if (currentTasks.length > 0 && currentTasks[0].text !== undefined) {
    //   hideEmptyGroups = true;
    // }

    // const [boardView]: BoardView[] = boardViewSelectByQuery(
    //   state.DBBoardViewReducer,
    //   { boardId: selectedBoardId, _id: currentBoardView.id }
    // );

    // let viewFilterList = viewFiltersSelectByQuery(
    //   state.DBViewFiltersReducer,
    //   {
    //     viewId: boardView?._id ?? selectedBoardId,
    //     $or: [{ deleted: { $exists: false } }, { deleted: false }],
    //   },
    //   null,
    //   {
    //     updatedAt: 1,
    //   }
    // );

    // if (
    //   state.TasksReducer.tasksFilter.text ||
    //   state.TasksReducer.personFilter ||
    //   viewFilterList.length > 0
    // ) {
    //   hideEmptyGroups = true;
    // }

    // Hide empty groups when there is a text filter
    if (textFilter) {
      hideEmptyGroups = true;
    }

    const elementsList = mergeGroupsAndTasks(
      selectedBoardId,
      currentGroups,
      currentTasks,
      hideEmptyGroups,
      isGroupDragging,
      toggleTasksMap
    );
    return elementsList;
  }
);

export const cellValueByTaskAndColumnId = (task: Task, column: TaskColumn) => {
  const columnType: TaskColumnType | undefined = columnTypeList.find(
    (columnType) => columnType.type === column?.type
  );
  // Use a custom cell value function or the default custom data selector if it is not defined
  let value;
  if (columnType?.cellValueFunction) {
    value = columnType.cellValueFunction(task);
  } else {
    const colId = column?._id;
    const customColumnData: CustomColumnData | undefined =
      task?.customData && colId !== undefined ? task.customData[colId] : undefined;
    value = customColumnData?.value;
  }
  return value;
};

export const cellValueSelector = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState, taskId, columnId) => ({ taskId, columnId }),
  ],
  (state: CommonRootState, { taskId, columnId }) => {
    const task = tasksSelectors.selectById(state, taskId);
    const column = taskColumnSelectors.selectById(state, columnId);

    // return columnType?.cellValueFunction ? columnType.cellValueFunction(task) : cellValueByTaskAndColumnId(task, column);
    return cellValueByTaskAndColumnId(task, column);
  }
);

export const getGroupListOnlyIdsAndNames = createSelector(
  [(state: CommonRootState) => state, getSelectedBoardId],
  (state: CommonRootState, boardId) => {
    const groupListOnlyIdsAndNames = taskGroupsSelectByQuery(
      state.DBTaskgroupsReducer,
      {
        boardId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      },
      ["_id", "name"]
    );

    return groupListOnlyIdsAndNames;
  }
);

export const getColumnTypeById = createSelector(
  [
    getSelectedBoardId,
    (state: CommonRootState, _columnId: string) => state,
    (_state: CommonRootState, columnId: string) => columnId,
  ],
  (_boardId, state, columnId) => {
    const columnList = getColumnList(state);
    const columnType = columnList.find((column) => columnId === column?._id);
    return columnType;
  }
);

export const columnListSelectorForKanban = createSelector(
  [
    getSelectedBoardId,
    (state: CommonRootState, kanbanId) => state,
    (state: CommonRootState, kanbanId) => kanbanId,
  ],
  (boardId, state, kanbanId) => {
    const includedColumns = kanbanSelectOneFieldByQuery(
      state.DBKanbanReducer,
      { _id: kanbanId },
      "includedColumns"
    ) as Record<string, boolean> | null | undefined;

    const dynamicColumnList = taskColumnSelectByQuery(
      state.DBTaskColumnReducer,
      {
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
        boardId,
      },
      ["_id", "type", "title"],
      {
        order: 1,
      }
    );

    let columnList;

    columnList = [...dynamicColumnList]
      .filter((column) => {
        const colId = column?._id;
        return includedColumns && colId !== undefined && includedColumns[colId];
      })
      .sort((colA, colB) => (colA?.order ?? 0) - (colB?.order ?? 0));

    return columnList.map(
      (column, index) =>
      ({
        _id: column?._id,
        type: column?.type,
        title: column?.title,
      } as TaskColumn)
    );
  }
);

export const getGroupIdForKanbanColumns = createSelector(
  [(state: CommonRootState) => state, getSelectedBoardId],
  (state: CommonRootState, boardId) => {
    const groupId = taskGroupsSelectOneFieldByQuery(
      state.DBTaskgroupsReducer,
      {
        boardId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      },
      ["_id"],
      {
        order: -1,
      }
    );

    return groupId;
  }
);

export const filterTaskColumns = (state: CommonRootState, boardId: string | undefined) =>
  taskColumnSelectByQuery(
    state.DBTaskColumnReducer,
    {
      $and: [
        { $or: [{ deleted: { $exists: false } }, { deleted: false }] },
        { $or: [{ isVisible: true }, { isVisible: { $exists: false } }] },
      ],
      boardId: boardId ?? "No Board", // if no board add a filter so no columns will be returned
    },
    [],
    {
      order: 1,
    }
  );

export const columnListSelectorForBoardView = createSelector(
  [getSelectedBoardId, (state: CommonRootState, _boardViewId: string) => state, (_state: CommonRootState, boardViewId: string) => boardViewId],
  (boardId, state, boardViewId) => {
    const includedColumns = boardViewSelectOneFieldByQuery(
      state.DBBoardViewReducer,
      { _id: boardViewId },
      "includedColumns"
    ) as Record<string, boolean> | null | undefined;

    const dynamicColumnList = taskColumnSelectByQuery(
      state.DBTaskColumnReducer,
      {
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
        boardId,
      },
      ["_id", "type", "title"],
      {
        order: 1,
      }
    );

    let columnList;
    columnList = [...dynamicColumnList]
      .filter((column) => {
        const colId = column?._id;
        return includedColumns && colId !== undefined && includedColumns[colId];
      })
      .sort((colA, colB) => (colA?.order ?? 0) - (colB?.order ?? 0));
    return columnList.map(
      (column, index) =>
      ({
        _id: column?._id,
        type: column?.type,
        title: column?.title,
      } as TaskColumn)
    );
  }
);

export const getBoardView = createSelector(
  [getSelectedBoardId, (state: CommonRootState) => state],
  (_, state) => {
    const boardId = getSelectedBoardId(state);

    const currentBoardView = state.TasksReducer.currentBoardView;
    const [boardViewId] = boardViewSelectByQuery(state.DBBoardViewReducer, {
      boardId: boardId,
      _id: currentBoardView.id,
    });
    return boardViewId;
  }
);

export const columnListForDisplaySelector = createSelector(
  [(state: CommonRootState) => state, filterTaskColumns, (_, __, groupId?) => groupId],
  (state: CommonRootState, dynamicColumnList, groupId) => {
    let columnList;
    let viewMode = state.TasksReducer.viewMode;

    if (viewMode === "tasks") {
      const boardId = getSelectedBoardId(state);
      const boardType = boardsSelectOneFieldById(state.DBBoardsReducer, boardId, "type");
      const isDisplayChat = boardType !== "audience";

      const currentBoardView = state.TasksReducer.currentBoardView;
      const boardViewId = boardViewSelectByQuery(state.DBBoardViewReducer, {
        boardId: boardId,
        _id: currentBoardView.id,
      });

      if (boardViewId.length > 0) {
        const includedBoardViewColumnList = columnListSelectorForBoardView(
          state,
          currentBoardView.id ?? ""
        );
        columnList = [...staticColumnList, ...includedBoardViewColumnList].sort(
          (colA, colB) => (colA.order ?? 0) - (colB.order ?? 0)
        );
      } else {
        const groupTaskListLength = getGroupTasksListWithoutSubTasks(
          state,
          groupId
        );
        const isCollapsed = taskGroupsSelectOneFieldByQuery(
          state.DBTaskgroupsReducer,
          {
            _id: groupId,
          },
          ["isCollapsed"]
        );

        columnList = [...staticColumnList, ...dynamicColumnList].sort(
          (colA, colB) => (colA.order ?? 0) - (colB.order ?? 0)
        );
        if (groupTaskListLength === 0 || isCollapsed) {
          columnList = columnList.splice(0, 2);
        }
        columnList = columnList.filter(col => (col.type === 'chat' && isDisplayChat) || (col.type !== 'chat'));
      }
    } else {
      const myWorkPreferences = state.MyWorkReducer.myWorkPreferences;
      columnList = getMyWorkColumnsList(myWorkPreferences, dynamicColumnList);
    }

    return columnList;
  }
);

type MyWorkColumn = TaskColumn & { width?: number; outerCell?: boolean };
type MyWorkPreferences = { selectedColumns?: Record<string, { status?: string; date?: string }> };
export const getMyWorkColumnsList = (myWorkPreferences: MyWorkPreferences | undefined, dynamicColumnList: TaskColumn[]) => {
  let columnList: MyWorkColumn[] = [];
  const desiredTypesOrder = [
    "person-picker",
    "datepicker",
    "status-option-picker",
  ];
  const newTitles: Record<string, string> = {
    "person-picker": "Owner",
    "status-option-picker": "Status",
    datepicker: "Date",
  };

  let foundTypes: Record<string, boolean> = {};
  const desiredColumns: MyWorkColumn[] = dynamicColumnList
    .filter((col) => {
      if (col.type && desiredTypesOrder.includes(col.type) && !foundTypes[col.type]) {
        foundTypes[col.type] = true;
        return true;
      }
      return false;
    })
    .map((col) => ({
      ...col,
      width: 180,
      title: col.type ? newTitles[col.type] : col.title,
    }));
  desiredColumns.sort(
    (a: MyWorkColumn, b: MyWorkColumn) =>
      desiredTypesOrder.indexOf(a.type ?? '') - desiredTypesOrder.indexOf(b.type ?? '')
  );
  // Insert 'empty-cell' columns for missing types
  desiredTypesOrder.forEach((type, index) => {
    if (!foundTypes[type]) {
      const insertPosition = desiredColumns.findIndex(
        (col) => desiredTypesOrder.indexOf(col.type ?? '') > index
      );

      const emptyColumn: MyWorkColumn = {
        _id: "empty-cell",
        type: "empty-cell" as ColumnType,
        title: newTitles[type],
        width: 180,
        static: true,
        outerCell: true,
        order: index,
      };

      if (insertPosition === -1) {
        desiredColumns.push(emptyColumn);
      } else {
        desiredColumns.splice(insertPosition, 0, emptyColumn);
      }
    }
  });

  if (myWorkPreferences?.selectedColumns) {
    desiredColumns.forEach((col, index) => {
      const selectedCol = myWorkPreferences?.selectedColumns?.[col.boardId ?? ''];
      if (selectedCol) {
        if (selectedCol.status && col.type === "status-option-picker") {
          const replacement = dynamicColumnList.find(
            (dCol) => dCol._id === selectedCol.status
          );
          if (replacement) {
            desiredColumns[index] = {
              ...replacement,
              width: 250,
              title: replacement.type ? newTitles[replacement.type] : replacement.title,
            };
          }
        } else if (selectedCol.date && col.type === "datepicker") {
          const replacement = dynamicColumnList.find(
            (dCol) => dCol._id === selectedCol.date
          );
          if (replacement) {
            desiredColumns[index] = {
              ...replacement,
              width: 100,
              title: replacement.type ? newTitles[replacement.type] : replacement.title,
            };
          }
        }
      }
    });
  }

  columnList = [...myWorkStaticColumnList, ...desiredColumns];

  return columnList;
};

export const dynamicColumnList = createSelector(
  [(state: CommonRootState, boardId: string | undefined) => columnListSelector(state, boardId)],
  (columnList: TaskColumn[]) => {
    return columnList.filter((column) => !column.static);
  }
);

export const getColumnsForDisplayForWorkloadUserCard = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState, boardId: string | undefined) => dynamicColumnList(state, boardId),
    (_state: CommonRootState, _boardId: string | undefined, workloadId: string) => workloadId,
  ],
  (state: CommonRootState, columnList: TaskColumn[], workloadId) => {
    const includedColumns = workloadSelectOneFieldByQuery(
      state.DBWorkloadReducer,
      {
        _id: workloadId,
      },
      "includedColumns"
    ) as Record<string, boolean> | null | undefined;

    const columnsForDisplay = columnList.filter(
      (column) => includedColumns && column._id !== undefined && includedColumns[column._id]
    );

    return columnsForDisplay;
  }
);

export const getOrderedTaskListForWorkloadUserCard = createSelector(
  [
    getSelectedBoardId,
    (state: CommonRootState) => state,
    (_state: CommonRootState, userId: string) => userId,
    (_state: CommonRootState, _userId: string, workloadId: string) => workloadId,
  ],
  (boardId, state, userId, workloadId) => {
    const personPickerId = workloadSelectOneFieldByQuery(
      state.DBWorkloadReducer,
      {
        _id: workloadId,
      },
      "personPickerId"
    ) as string | undefined;

    const _tasksList = tasksSelectByQuery(
      state.DBTasksReducer,
      {
        boardId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      },
      [],
      {}
    );

    const tasksOrderByUser = workloadSelectOneFieldByQuery(
      state.DBWorkloadReducer,
      {
        _id: workloadId,
      },
      "tasksOrderByUser"
    ) as Record<string, Record<string, number>> | null | undefined;

    const isUserInTask = (task: Task, userId: string) => {
      if (task.customData !== undefined && personPickerId !== undefined) {
        if (task.customData[personPickerId] !== undefined) {
          if (task.customData[personPickerId].value !== undefined) {
            if (task.customData[personPickerId].value.length !== 0) {
              let counter = 0;
              for (
                let i = 0;
                i < task.customData[personPickerId].value.length;
                i++
              ) {
                if (task.customData[personPickerId].value[i].id === userId) {
                  counter++;
                }
              }
              if (counter !== 0) {
                return true;
              }
            } else {
              return userId === "emptystateuserfordisplay";
            }
          } else {
            return userId === "emptystateuserfordisplay";
          }
        } else {
          return userId === "emptystateuserfordisplay";
        }
      } else {
        return false;
      }
    };

    const setTaskOrderForSort = (taskId: string, length: number) => {
      if (tasksOrderByUser) {
        if (tasksOrderByUser[userId] !== undefined) {
          const userOrder = tasksOrderByUser[userId];
          if (userOrder[taskId] !== undefined) {
            return userOrder[taskId];
          }
        }
      } else {
        return length;
      }
    };

    const tasksList = _tasksList
      .filter((task) => isUserInTask(task, userId))
      .sort((a, b) => {
        const tasksLength = _tasksList.length;
        const aOrder = setTaskOrderForSort(a._id ?? '', tasksLength) ?? tasksLength;
        const bOrder = setTaskOrderForSort(b._id ?? '', tasksLength) ?? tasksLength;
        return aOrder - bOrder;
      });

    return tasksList.map(
      (task) => ({ _id: task._id, customData: task.customData } as Task)
    );
  }
);

export const getUserCapacityList = createSelector(
  [
    (state: CommonRootState, _userId: string, _workloadId: string, _complexityPickerId: string) => state,
    (_state: CommonRootState, userId: string, _workloadId: string, _complexityPickerId: string) => userId,
    (_state: CommonRootState, _userId: string, workloadId: string, _complexityPickerId: string) => workloadId,
    (_state: CommonRootState, _userId: string, _workloadId: string, complexityPickerId: string) => complexityPickerId,
    getSelectedBoardId,
  ],
  (state: CommonRootState, userId, workloadId, complexityPickerId, boardId) => {
    const tasksList = getOrderedTaskListForWorkloadUserCard(
      state,
      userId,
      workloadId
    );

    const columnOptions = taskColumnOptionSelectByQuery(
      state.DBTaskColumnOptionReducer,
      {
        boardId,
        columnId: complexityPickerId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      }
    );

    const workloadPeople = workloadSelectOneFieldByQuery(
      state.DBWorkloadReducer,
      {
        _id: workloadId,
      },
      "people"
    ) as Record<string, { capacity?: number }> | null | undefined;

    const isUserInWorkloadPeople = (userId: string) => {
      if (workloadPeople && workloadPeople[userId]) {
        return true;
      } else {
        return false;
      }
    };

    const _defaultCapacity = workloadSelectOneFieldByQuery(
      state.DBWorkloadReducer,
      {
        _id: workloadId,
      },
      "defaultCapacity"
    ) as number | undefined;

    const defaultCapacity =
      _defaultCapacity === undefined ? 100 : _defaultCapacity;
    let profileCapacity;
    if (isUserInWorkloadPeople(userId) && workloadPeople) {
      profileCapacity = workloadPeople[userId].capacity ?? defaultCapacity;
    } else {
      profileCapacity = defaultCapacity;
    }

    const userCapacityList = tasksList.map((task, index) => {
      const option = columnOptions.find(
        (option) =>
          task.customData !== undefined &&
          task.customData[complexityPickerId] !== undefined &&
          task.customData[complexityPickerId].value.toString() !== "" &&
          option._id === task.customData[complexityPickerId].value
      );
      const taskComplexityPercentage =
        ((option?.complexity ?? 0) / profileCapacity) * 100;
      const taskComplexityColor =
        option?.bgcolor ?? "var(--color-surface)";
      return [taskComplexityPercentage, taskComplexityColor];
    });

    return userCapacityList;
  }
);

export const getTaskComplexityList = createSelector(
  [
    (state: CommonRootState, _userId: string, _workloadId: string, _complexityPickerId: string) => state,
    (_state: CommonRootState, userId: string, _workloadId: string, _complexityPickerId: string) => userId,
    (_state: CommonRootState, _userId: string, workloadId: string, _complexityPickerId: string) => workloadId,
    (_state: CommonRootState, _userId: string, _workloadId: string, complexityPickerId: string) => complexityPickerId,
    getSelectedBoardId,
  ],
  (state: CommonRootState, userId, workloadId, complexityPickerId, boardId) => {
    const tasksList = getOrderedTaskListForWorkloadUserCard(
      state,
      userId,
      workloadId
    );

    const columnOptions = taskColumnOptionSelectByQuery(
      state.DBTaskColumnOptionReducer,
      {
        boardId,
        columnId: complexityPickerId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      }
    );

    const taskComplexityList = tasksList.map((task, index) => {
      const option = columnOptions.find(
        (option) =>
          task.customData !== undefined &&
          task.customData[complexityPickerId] !== undefined &&
          task.customData[complexityPickerId].value.toString() !== "" &&
          option._id === task.customData[complexityPickerId].value
      );
      return option?.complexity ?? 0;
    });

    return taskComplexityList;
  }
);

export const getProfileCapacity = createSelector(
  [
    (state: CommonRootState, _workloadId: string, _userId: string) => state,
    (_state: CommonRootState, workloadId: string, _userId: string) => workloadId,
    (_state: CommonRootState, _workloadId: string, userId: string) => userId,
  ],
  (state, workloadId, userId) => {
    const workloadPeople = workloadSelectOneFieldByQuery(
      state.DBWorkloadReducer,
      {
        _id: workloadId,
      },
      "people"
    ) as Record<string, { capacity?: number }> | null | undefined;
    const _defaultCapacity = workloadSelectOneFieldByQuery(
      state.DBWorkloadReducer,
      {
        _id: workloadId,
      },
      "defaultCapacity"
    ) as number | undefined;

    const defaultCapacity =
      _defaultCapacity === undefined ? 100 : _defaultCapacity;
    const isUserInWorkloadPeople = (userId: string) => {
      if (workloadPeople && workloadPeople[userId]) {
        return true;
      } else {
        return false;
      }
    };

    let profileCapacity;
    if (isUserInWorkloadPeople(userId) && workloadPeople) {
      profileCapacity = workloadPeople[userId].capacity ?? defaultCapacity;
    } else {
      profileCapacity = defaultCapacity;
    }

    return profileCapacity;
  }
);

export const getDefaultCapacity = createSelector(
  [(state: CommonRootState, _workloadId: string) => state, (_state: CommonRootState, workloadId: string) => workloadId],
  (state, workloadId) => {
    const _defaultCapacity = workloadSelectOneFieldByQuery(
      state.DBWorkloadReducer,
      {
        _id: workloadId,
      },
      "defaultCapacity"
    );

    const defaultCapacity =
      _defaultCapacity === undefined ? 100 : _defaultCapacity;

    return defaultCapacity;
  }
);

export const getDynamicColumnList = createSelector(
  [getSelectedBoardId, (state: CommonRootState) => state],
  (boardId, state) => {
    const columnList = dynamicColumnList(state, boardId);
    return columnList.filter((column) => !column.static);
  }
);

export const getDynamicColumnListForBTCheckbox = createSelector(
  [(state: CommonRootState, displayMode) => state, (_, displayMode) => displayMode],
  (state: CommonRootState, displayMode) => {
    let columnList: TaskColumn[];
    if (displayMode === "board") {
      columnList = getDynamicColumnList(state).map(
        (column, index) =>
        ({
          _id: column._id,
          title: column.title,
          type: column.type,
          isVisible: column.isVisible,
        } as TaskColumn)
      );
    } else {
      columnList = getDynamicColumnList(state).map(
        (column, index) =>
        ({
          _id: column._id,
          title: column.title,
          type: column.type,
        } as TaskColumn)
      );
    }

    return columnList;
  }
);

export const timelinePickerColumnList = createSelector(
  [(state: CommonRootState, boardId: string | undefined) => columnListSelector(state, boardId)],
  (columnList: TaskColumn[]) => {
    return columnList.filter((column) => column.type === "timeline-picker");
  }
);

export const optionColumnList = createSelector(
  [(state: CommonRootState) => state],
  (state: CommonRootState) => {
    const boardId = getSelectedBoardId(state);

    const columnList = columnListSelector(state, boardId).map(
      (column, index) =>
      ({
        type: column.type,
        _id: column._id,
        title: column.title,
      } as TaskColumn)
    );

    return columnList.filter(
      (column) =>
        column.type === "option-picker" ||
        column.type === "status-option-picker"
      // || column.type === 'complexity-picker'
    );
  }
);

export const selectColumnListByType = createSelector(
  [
    (_state: CommonRootState, _boardId: string | undefined, type: string) => type,
    (state: CommonRootState, boardId: string | undefined, _type: string) => columnListSelector(state, boardId),
  ],
  (type: string, columnList: TaskColumn[]) => {
    return columnList.filter((column) => column.type === type);
  }
);

export const dynamicColumnListForForms = createSelector(
  [(state: CommonRootState, boardId: string | undefined) => dynamicColumnList(state, boardId)],
  (columnList: TaskColumn[]) => {
    let returnList = columnList.filter((column) => {
      const cell = columnTypeList.find(
        (columnType) => columnType.type === column.type
      );
      return cell?.isCellInForms;
    });
    const nameTask: TaskColumn = {
      _id: "name",
      type: "row-prefix",
      title: "Task Name",
    };
    returnList = [nameTask, ...returnList];

    return returnList;
  }
);

export const dynamicColumnsListOfIdsTypesAndTitles = createSelector(
  [(state: CommonRootState, boardId: string | undefined) => dynamicColumnList(state, boardId)],
  (columnList: TaskColumn[]): Array<[string, string, string?]> => {
    const returnList: Array<[string, string, string?]> = columnList.map((column) => {
      return [column._id ?? '', column.type ?? '', column.title] as [string, string, string?];
    });

    return returnList;
  }
);

export const dynamicColumnFilteredById = createSelector(
  [
    (state: CommonRootState, { columnId }) => ({ columnId }),
    (state: CommonRootState, { boardId }) => dynamicColumnList(state, boardId),
  ],
  ({ columnId }, columnList: TaskColumn[]) => {
    return columnList.find((column) => {
      return column._id === columnId;
    });
  }
);

//get sort object from selected board
export const getBoard = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState) => {
    const { selectedBoardId } = state.BoardsReducer;
    return boardsSelectors.selectById(state, selectedBoardId);
  }
);

export const getSelectedPersons = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState) => {
    return state.TasksReducer.personFilter;
  }
);

export const getAssignedUserListToTasks = createSelector(
  [(state: CommonRootState) => state, baseTasks, getSelectedBoardId],
  (state: CommonRootState, tasks, boardId) => {
    let currentAssignedUsers: any[] = [];
    let filteredAssignedUserList: any[] = [];
    if (tasks.length > 0) {
      const columnList = columnListSelector(state, boardId);
      const personPickerColumns = columnList.filter((obj) => {
        return obj.type === "person-picker";
      });
      const personPickerColumnsIds = personPickerColumns.map(({ _id }) => _id);
      if (personPickerColumnsIds.length > 0) {
        for (let i = 0; i < tasks.length; i++) {
          personPickerColumnsIds.forEach(function (item) {
            if (item === undefined) return;
            const currentUserAssigned = tasks[i]?.customData?.[item]?.value;
            if (currentUserAssigned) {
              currentAssignedUsers.push(currentUserAssigned);
            }
          });
        }
      }
    }
    if (currentAssignedUsers.length > 0) {
      filteredAssignedUserList = currentAssignedUsers
        .flatMap((element) => element)
        .filter(
          (user, index, array) =>
            array.findIndex((t) => t?.id === user?.id) === index
        )
        .map((user) => user.id);
    }

    const selectedBoardUsers = boardUserSelectByQuery(
      state.DBBoardUserReducer,
      {
        _id: {
          $in: filteredAssignedUserList,
        },
      }
    );

    const _mockPersonList = taskColumnOptionSelectByQuery(
      state.DBTaskColumnOptionReducer,
      {
        _id: {
          $in: filteredAssignedUserList,
        },
        boardId,
        imageUrl: { $exists: true },
      }
    );
    const mockPersonList = _mockPersonList.map((option) => {
      const [firstName, lastName] = option.label.split(" ");
      const workspaceUser: AugmentedBlueticksBoardUser = {
        _id: option._id,
        status: "active",
        profile: {
          firstName,
          lastName,
          profileImg: option.imageUrl,
        } as User,
        type: "option",
      };
      return workspaceUser;
    });
    const assignedUsersAndMockUser = [...selectedBoardUsers, ...mockPersonList];
    return assignedUsersAndMockUser;
  }
);

export const areAllGroupsCollapsedSelector = createSelector(
  [(state: CommonRootState) => state, currentGroups],
  (state: CommonRootState, currentGroupList) => {
    return currentGroupList.every((group) => group.isCollapsed);
  }
);

export const getPreviousOrNextTaskIdByCurrentTaskId = createSelector(
  [
    selectElementsList,
    (state: CommonRootState, taskId, type) => taskId,
    (state: CommonRootState, taskId, type) => type,
  ],
  (allElements, taskId, type) => {
    const elementList = allElements.filter(
      (e) => e?.type === "task"
    );
    const numberOfElements = elementList.length;
    const foundEl = elementList.find((e) => e?.id === taskId);
    const currentTaskIndex: number = foundEl ? elementList.indexOf(foundEl) : -1;
    if (type === "previous") {
      if (currentTaskIndex === 0) {
        return elementList[numberOfElements - 1]?.id;
      } else {
        return elementList[currentTaskIndex - 1]?.id;
      }
    } else {
      if (currentTaskIndex === numberOfElements - 1) {
        return elementList[0]?.id;
      } else {
        return elementList[currentTaskIndex + 1]?.id;
      }
    }
  }
);

export const selectOrderedBoardTasksAndSubtasks = createSelector(
  [(state: CommonRootState) => state.DBTasksReducer.entities, selectElementsList],
  (taskEntities, elementsList) => {
    const taskList = elementsList
      .filter(
        (element) =>
          [BoardRowType.task, BoardRowType.subTask].indexOf(element.type) > -1
      )
      .map((element) => element.taskId !== undefined ? taskEntities[element.taskId] : undefined);

    return taskList;
  }
);

export const getCurrentGroupsLength = createSelector(
  currentGroups,
  (currentGroups): number => {
    return currentGroups.length;
  }
);

export const getExtradataByTaskId = createSelector(
  [(state: CommonRootState) => state, (_: CommonRootState, taskId: string) => taskId],
  (state: CommonRootState, taskId) => {
    const extraData = tasksExtraDataSelectByQuery(
      state.DBTasksExtraDataReducer,
      {
        taskId: taskId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      },
      ["_id"],
      { createdAt: -1 }
    );
    return extraData;
  }
);

export const getTaskExtradataLength = createSelector(
  getExtradataByTaskId,
  (extraData): number => {
    return extraData.length ?? 0;
  }
);

export const getExtraDataMergedFiles = createSelector(
  [(state: CommonRootState) => state],
  (state: CommonRootState) => {
    const boardId = state.BoardsReducer.selectedBoardId;
    const displayExtraDataOfTaskById =
      state.ExtraDataReducer.displayExtraDataOfTaskById;
    let mergedData: any;
    const filesColumns = taskColumnSelectByQuery(state.DBTaskColumnReducer, {
      boardId: boardId,
      type: "files",
      $or: [{ deleted: { $exists: false } }, { deleted: false }],
    });

    const filteredRes: NullableId[] = filesColumns.map((item) => item._id ?? null);

    const task = tasksSelectOneObjectById(state.DBTasksReducer, displayExtraDataOfTaskById);

    const extraData = tasksExtraDataSelectByQuery(
      state.DBTasksExtraDataReducer,
      {
        taskId: displayExtraDataOfTaskById,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      }
    );

    const extraDataFiles = _tasksExtraDataFilesSelectOneObjectByQuery(
      state.DBTasksExtraDataFilesReducer,
      {
        taskId: displayExtraDataOfTaskById,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      }
    );

    mergedData = mergeData(task, filteredRes, extraData, extraDataFiles);

    return mergedData ?? [];
  }
);

function mergeData(
  task: Task | undefined,
  filesColumnsIds: NullableId[],
  extraDataItems: ExtraDataItem[],
  extraDataFiles: ExtraDataFile | undefined
) {
  let tasksFilesCells: any[] = [];
  let taskUpdates: any[] = [];
  let taskGeneralFiles: any[] = [];

  // Process filesColumnsIds
  filesColumnsIds.forEach((fileColumnId) => {
    if (fileColumnId === null || fileColumnId === undefined) return;
    const data = task?.customData?.[fileColumnId]?.value;
    if (data) {
      tasksFilesCells.push(...data);
    }
  });

  function extractUrlsFromChildren(children: Array<{ type?: string; url?: string; children?: unknown[] }>) {
    let urls: Array<{ type: string; url: string }> = [];
    children.forEach((child) => {
      if (child.type === "link" && child.url) {
        urls.push({
          type: "link",
          url: child.url,
        });
      }
      if (child.children) {
        urls.push(...extractUrlsFromChildren(child.children as Array<{ type?: string; url?: string; children?: unknown[] }>));
      }
    });
    return urls;
  }

  // Process extraDataItems for Task Updates
  if (extraDataItems && extraDataItems.length > 0) {
    extraDataItems.forEach((item) => {
      if (item.extraData) {
        item.extraData.forEach((extraItem) => {
          if ("url" in extraItem) {
            taskUpdates.push({
              type: extraItem.type,
              url: extraItem.url,
            });
          }

          // if (extraItem && "children" in extraItem && extraItem.children) {
          //   const extractedUrls = extractUrlsFromChildren(extraItem.children);
          //   taskUpdates.push(...extractedUrls);
          // }
        });
      }
    });
  }

  // Process extraDataFiles for Task General Files
  if (extraDataFiles && extraDataFiles.files.length > 0) {
    taskGeneralFiles.push(...extraDataFiles.files);
  }

  return {
    tasks: tasksFilesCells,
    updates: taskUpdates,
    generalFiles: taskGeneralFiles,
  };
}

export const getIncludedColumnsByBoardId = createSelector(
  [
    (state: CommonRootState, boardId: any) => state,
    (state: CommonRootState, boardId: any) => boardId,
  ],
  (state: CommonRootState, boardId) => {
    const dynamicColumnList = taskColumnSelectByQuery(
      state.DBTaskColumnReducer,
      {
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
        boardId,
      },
      ["_id", "isVisible"],
      {
        order: 1,
      }
    );

    return dynamicColumnList;
  }
);

export const getGroupsMaxOrder = createSelector(
  [(state: CommonRootState) => state],
  (state: CommonRootState) => {
    const boardId = state.BoardsReducer.selectedBoardId;
    return taskGroupsSelectByQueryOnlyMaxMin(
      state.DBTaskgroupsReducer,
      {
        boardId: boardId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      },
      "order",
      "max"
    );
  }
);

export const getPersonById = createSelector(
  [getSelectedBoardCompleteUserList, getSelectedPersons],
  (personList, personId) => {
    return personList.find((p) => p._id === personId);
  }
);

export const getGroupTasksListWithoutSubTasks = createSelector(
  [(state: CommonRootState) => state, (_, groupId) => ({ groupId })],
  (state: CommonRootState, { groupId }) => {
    const groupTasksLength = tasksSelectByQueryOnlyLength(
      state.DBTasksReducer,
      {
        groupId: groupId,
        parentTaskId: { $exists: false },
      }
    );
    return groupId ? groupTasksLength : "";
  }
);

export const kanbanColumnOptionsSorted = createSelector(
  [
    getSelectedBoardId,
    (state: CommonRootState, kanbanId) => state,
    (state: CommonRootState, kanbanId) => kanbanId,
  ],
  (boardId, state, kanbanId) => {
    const optionColumnId = kanbanSelectOneFieldByQuery(
      state.DBKanbanReducer,
      { _id: kanbanId },
      "optionColumnId"
    ) as NullableId;
    const columnOptions: TaskColumnOption[] = taskColumnOptionSelectByQuery(
      state.DBTaskColumnOptionReducer,
      {
        boardId,
        columnId: optionColumnId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      },
      undefined,
      {
        order: 1,
      }
    );
    const kanban = kanbanSelectOneObjectByQuery(state.DBKanbanReducer, {
      boardId,
      _id: kanbanId,
    });
    const sortedOptions = columnOptions
      .sort((a, b) => {
        if (kanban?.columnsOrder) {
          const numberOfOptions = columnOptions.length;
          const aId = a._id ?? '';
          const bId = b._id ?? '';

          let aOrder = numberOfOptions;
          if (kanban.columnsOrder[aId] !== undefined) {
            aOrder = kanban.columnsOrder[aId];
          }

          let bOrder = numberOfOptions;
          if (kanban.columnsOrder[bId] !== undefined) {
            bOrder = kanban.columnsOrder[bId];
          }
          return aOrder - bOrder;
        } else {
          return (a.order ?? 0) - (b.order ?? 0);
        }
      })
      .map((option, index) => ({
        ...option,
        order: index,
      }));
    return sortedOptions;
  }
);

export const isKanbanOptionPickerExists = createSelector(
  [(state: CommonRootState) => state, (_, kanbanId) => kanbanId],
  (state: CommonRootState, kanbanId) => {
    const optionColumnId = kanbanSelectOneFieldByQuery(
      state.DBKanbanReducer,
      { _id: kanbanId },
      "optionColumnId"
    );
    return optionColumnId !== undefined;
  }
);

export const isWorkloadComplexityPickerExist = createSelector(
  [(state: CommonRootState) => state, (_state: CommonRootState, workloadId: string) => workloadId],
  (state, workloadId) => {
    const complexityPickerId = workloadSelectOneFieldByQuery(
      state.DBWorkloadReducer,
      { _id: workloadId },
      "complexityPickerId"
    );
    return !(complexityPickerId === null || complexityPickerId === undefined);
  }
);

export const isWorkloadPersonPickerExist = createSelector(
  [(state: CommonRootState) => state, (_state: CommonRootState, workloadId: string) => workloadId],
  (state, workloadId) => {
    const personPickerId = workloadSelectOneFieldByQuery(
      state.DBWorkloadReducer,
      { _id: workloadId },
      "personPickerId"
    );
    return !(personPickerId === null || personPickerId === undefined);
  }
);

export const workloadSortedUserList = createSelector(
  [
    (state: CommonRootState, workloadId) => state,
    (state: CommonRootState, workloadId) => workloadId,
    getSelectedBoardId,
  ],
  (state: CommonRootState, workloadId, boardId) => {
    const emptyStateUser: AugmentedBlueticksBoardUser = {
      _id: "emptystateuserfordisplay", // 24 letters
      type: "user",
      boardId,
      name: "Empty Tasks",
      firstName: "Empty",
      lastName: "Tasks",
    };
    // const mockPersonList = getMockPersonList(state)
    const userList = [
      ...getSelectedBoardCompleteUserList(state),
      emptyStateUser,
    ];
    const usersOrder = (workloadSelectOneFieldByQuery(
      state.DBWorkloadReducer,
      {
        _id: workloadId,
      },
      "usersOrder"
    ) ?? {}) as Record<string, number>;

    const allKeys = Object.keys(usersOrder);
    const notFound = userList.filter((user) => user._id !== undefined && !allKeys.includes(user._id));

    const returnList: AugmentedBlueticksBoardUser[] = [];

    if (Object.keys(usersOrder).length !== 0) {
      for (const key in usersOrder) {
        if (Object.prototype.hasOwnProperty.call(usersOrder, key)) {
          const idx = usersOrder[key];
          const usr = userList.find((_usr) => _usr._id === key);
          if (usr) {
            returnList[idx] = usr;
          }
        }
      }

      let _returnList = returnList.filter((x) => x);
      _returnList.push(...notFound);
      return _returnList;
      // returnList = userList.sort((a, b) => {
      //   const numberOfUsers = userList.length
      //   const aOrder = usersOrder[a._id] === undefined ? (numberOfUsers + 1) : usersOrder[a._id]
      //   const bOrder = usersOrder[b._id] === undefined ? (numberOfUsers + 1) : usersOrder[b._id]
      //   return aOrder - bOrder
      //   // return bOrder - aOrder
      // })
      // return returnList
    } else {
      return userList;
    }
  }
);

export const getWorkloadUserForUserCard = createSelector(
  [(userId) => userId, (_, state) => state, (_, __, workloadId) => workloadId],
  (userId, state, workloadId) => {
    const userList = workloadSortedUserList(state, workloadId);

    return userList.find((user) => user._id === userId);
  }
);

export const getIsShowSubTasks = createSelector(
  [(params: { state: CommonRootState; taskId: string }) => params],
  (params) => {
    return params.state.TasksReducer.toggleTasksMap[params.taskId];
  }
);

export const getUserForLogs = createSelector(
  [(state: CommonRootState, value) => state, (state: CommonRootState, value) => value],
  (state: CommonRootState, value) => {
    const allUsersList = [
      ...getSelectedBoardCompleteUserList(state),
      ...getMockPersonList(state),
    ];
    const user = allUsersList.find((user) => user.userId === value?._id)
      ?.profile ??
      allUsersList.find((user) => user._id === value._id)?.profile ?? {
      firstName: value.firstName,
      lastName: value.lastName,
      email: "www@www.www",
    };

    return user as User;
  }
);

export const getTaskAccentColor = createSelector(
  [
    (type) => type,
    (_, groupId) => groupId,
    (_, __, state) => state,
    (_, __, ___, taskId) => taskId,
    (_, __, ___, ____, columnId) => columnId,
  ],
  (type, groupId, state, taskId, columnId) => {
    let groupColor;
    if (type === "kanban") {
      groupColor = taskGroupsSelectOneObjectByQuery(
        state.DBTaskgroupsReducer,
        {
          _id: groupId,
        }

      );
    }
    if (type === "workload") {
      const customData = tasksSelectOneFieldById(
        state.DBTasksReducer,
        taskId,
        "customData"
      );
      let complexityOptionId;
      if (customData !== null && customData !== undefined) {
        complexityOptionId = (customData as Record<string, { value: string }>)[columnId];
      }
      let complexityOption;
      if (complexityOptionId) {
        complexityOption = taskColumnOptionSelectOneObjectById(
          state.DBTaskColumnOptionReducer,
          complexityOptionId.value
        );
      }
      groupColor = complexityOption?.bgcolor;
    }

    return groupColor;
  }
);

export const getOneFieldFromTaskGroupByTaskId = createSelector(
  [(params: { state: CommonRootState; taskId: string; field: string }) => params],
  (params) => {

    const task: Task | undefined = tasksSelectors.selectById(params.state, params.taskId)
    const groupId = task?.groupId

    const selectedField = taskGroupsSelectOneFieldById(params.state.DBTaskgroupsReducer, groupId, params.field)

    return selectedField;
  }
);

//selector that returns checked tasks bool
export const getIsChecked = createSelector(
  [(params: { state: CommonRootState; taskId: string }) => params],
  (params) => {
    const bulkTasksMap = params.state.BulkActionsReducer.bulkTasksMap;
    const task = tasksSelectors.selectById(params.state, params.taskId);
    return !!bulkTasksMap[task?._id ?? ""];
  }
);

// selector that counts subtasks
export const getSubTasksCount = createSelector(
  [(state: CommonRootState) => state, (_, taskId) => taskId],
  (state: CommonRootState, taskId) => {
    const subtasksOfTheSameTask = tasksSelectByQuery(state.DBTasksReducer, {
      parentTaskId: taskId,
    });
    return subtasksOfTheSameTask.length;
  }
);

// selector for taskcheck for UI only
export const isLastTaskInGroup = createSelector(
  (state: CommonRootState, taskId) => ({ state, taskId }),
  selectElementsList,
  (params: any, elementsList: any) => {
    if (!params.taskId) return false;
    var tasks = elementsList.filter((el: { type?: BoardRowType }) => el?.type === BoardRowType.task);
    const task = tasks.find((el: { id?: string }) => el?.id === params.taskId);
    tasks = tasks.filter((el: { groupId?: string }) => el?.groupId === task?.groupId);
    return tasks.length && tasks[tasks.length - 1].id === params.taskId;
  }
);

// export const isLastTaskWithSubtasksInGroup = createSelector(
//   (state: CommonRootState, taskId, subTasksCount) => ({ state, taskId, subTasksCount }),
//   selectElementsList,
//   (params, elementsList) => {
//     if (!params.taskId) return false;
//     var tasks = elementsList.filter((el) => el?.type === BoardRowType.task);
//     const task = tasks.find((el) => el?.id === params.taskId);
//     tasks = tasks.filter((el) => {
//       return el?.groupId === task?.groupId
//     });
//     tasks = tasks.filter((el) => tasks.indexOf(el) <= tasks.indexOf(task) || tasks.indexOf(el) > tasks.indexOf(task) + params.subTasksCount)
//     return tasks.length && tasks[tasks.length - 1].id == params.taskId;
//   }
// );

export const isFirstTaskInGroup = createSelector(
  (state: CommonRootState, taskId) => ({ state, taskId }),
  selectElementsList,
  (params: any, elementsList: any) => {
    var tasks = elementsList.filter((el: { type?: BoardRowType }) => el?.type === BoardRowType.task);
    const task = tasks.find((el: { id?: string }) => el?.id === params.taskId);
    tasks = tasks.filter((el: { groupId?: string }) => el?.groupId === task?.groupId);
    return tasks.length && tasks[0].id === params.taskId;
  }
);

export const handleSubTasks = (isChecking: boolean, taskSubTasks: Task[], bulkTasksMap: Record<string, Task>) => {
  let uncheckParent = false;
  for (let subTask of taskSubTasks) {
    const subId = subTask._id ?? '';
    if (isChecking) {
      bulkTasksMap[subId] = subTask;
    } else {
      if (bulkTasksMap[subId]) {
        delete bulkTasksMap[subId];
        uncheckParent = true;
      }
    }
  }
  return {
    anchorId: isChecking
      ? taskSubTasks[taskSubTasks.length - 1]._id
      : undefined,
    uncheckParent,
  };
};

export const handleTaskSelection = (isChecking: boolean, taskId: string, task: Task, bulkTasksMap: Record<string, Task>) => {
  isChecking ? (bulkTasksMap[taskId] = task) : delete bulkTasksMap[taskId];
  return isChecking ? taskId : undefined;
};

export const selectDropdownOptionsAndPickedValues = createSelector(
  [(state: CommonRootState) => state, (state: CommonRootState, params: any) => params],
  (state: CommonRootState, params) => {
    const { taskId, columnId, boardId } = params;
    const cellValue = cellValueSelector(state, taskId, columnId);
    const columnOptions = taskColumnOptionSelectByQuery(
      state.DBTaskColumnOptionReducer,
      {
        boardId,
        columnId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      }
    );

    const dropdownValues = tasksSelectByQuery(state.DBTasksReducer, {
      boardId,
      [`customData.${columnId}`]: {
        $exists: true,
      },
    });
    const flattenDropdownValuesIds = dropdownValues
      .map((obj) => obj?.customData?.[columnId]?.value)
      .flat();
    const dropdownValuesIds = flattenDropdownValuesIds.map(
      (value) => value?._id
    );
    const extractValues = columnOptions
      .filter((option) => cellValue?.some((value: { _id: string }) => value._id === option._id))
      .map((s) => s._id);
    const optionsTopick = columnOptions.filter(
      (option) => !extractValues.includes(option._id)
    );
    const pickedOptions = columnOptions.filter((option) =>
      extractValues.includes(option._id)
    );

    return { columnOptions, optionsTopick, pickedOptions, dropdownValuesIds };
  }
);

export const columnOptionIsInUsed = createSelector(
  [(state: CommonRootState) => state, (state: CommonRootState, params: any) => params],
  (state: CommonRootState, params) => {
    const { columnId, boardId } = params;

    const tasks = tasksSelectByQuery(
      state.DBTasksReducer,
      {
        boardId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      }
    );

    const columnOptions = taskColumnOptionSelectByQuery(
      state.DBTaskColumnOptionReducer,
      {
        boardId,
        columnId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      }
    );

    const columnOptionsIds = columnOptions.map((option) => option._id);
    const columnValues = tasks
      .map((obj) => obj?.customData?.[columnId]?.value)
      .flat();

    const columnValuesIds = columnValues.map((value) => value);

    const columnOptionsInUse = columnOptionsIds.filter((option) =>
      columnValuesIds.includes(option)
    );
    return columnOptionsInUse;
  }
);


export const getColumnValues = createSelector(
  [(state: CommonRootState) => state, (state: CommonRootState, params: any) => params],
  (state: CommonRootState, params) => {
    const { groupId, columnId, boardId, columnType } = params;
    const currentBoardTasks = currentTasks(state);
    const currentBoardTaskIds = currentBoardTasks.map((task) => task._id);

    const columnValues = tasksSelectByQuery(state.DBTasksReducer, {
      boardId,
      groupId,
      taskType: { $ne: "subTask" },
      [`customData.${columnId}`]: {
        $exists: true,
      },
    }).filter((task) => currentBoardTaskIds.includes(task._id));

    const tasksLength = tasksSelectByQueryOnlyLength(state.DBTasksReducer, {
      boardId,
      groupId,
      taskType: { $ne: "subTask" },
    });
    let res;
    const columnValuesArray = columnValues
      .map((obj) => obj?.customData?.[columnId]?.value)
      .flat();
    if (columnType === "number" || columnType === "checkbox") {
      res = columnValuesArray;
    }
    if (columnType === "checkbox") {
      res = { length: tasksLength, data: columnValuesArray };
    }
    if (columnType === "person-picker") {
      res = columnValuesArray;
    }
    if (columnType === "rating") {
      res = { length: tasksLength, data: columnValuesArray };
    }
    if (
      columnType === "status-option-picker" ||
      columnType === "option-picker" ||
      columnType === "complexity-picker"
    ) {
      const tasks = tasksSelectByQuery(state.DBTasksReducer, {
        groupId,
        taskType: { $ne: "subTask" },
      }).filter((task) => currentBoardTaskIds.includes(task._id));
      const tasksLength = tasks.length;
      const columnOptions = taskColumnOptionSelectByQuery(
        state.DBTaskColumnOptionReducer,
        {
          boardId,
          columnId,
          $or: [{ deleted: { $exists: false } }, { deleted: false }],
        }
      );

      const count: Record<string, number> = {};
      columnValuesArray.forEach((element) => {
        const key = String(element);
        count[key] = (count[key] || 0) + 1;
      });

      const selectedOptions = columnValuesArray.map((id) =>
        columnOptions.find((el) => el._id === id)
      );

      const selectedOptionsCloned = JSON.parse(JSON.stringify(selectedOptions));
      const duplicates = findDuplicates(selectedOptionsCloned);
      if (duplicates) {
        selectedOptionsCloned.forEach((obj: { _id?: string; count?: number } | null) => {
          const count = duplicates[obj?._id ?? ''];
          if (obj && count && count >= 1) {
            obj.count = count;
          }
        });
      }
      res = { selectedOptionsCloned, tasksLength };
    }

    if (columnType === "files") {
      let allImages: any[] = [];
      columnValues?.flatMap((item) => {
        const files =
          item.customData?.[columnId]?.value?.map((obj: object) => ({
            ...obj,
            _id: item._id,
          })) ?? [];
        if (files.length > 0) {
          allImages.push(files);
        }
      });
      res = allImages.flat();
    }

    return res;
  }
);

export const getTasksWithValueInColumnIdSelector = createSelector(
  [(state: CommonRootState) => state, (_, columnId: NullableId) => columnId],
  (state: CommonRootState, columnId) => {
    const currentBoardTasks = currentTasks(state);
    const matchingList: Task[] = [];
    for (let i = 0; i < currentBoardTasks.length; i++) {
      const task = currentBoardTasks[i];
      if (columnId !== null && columnId !== undefined && task?.customData && task.customData[columnId]?.value) {
        matchingList.push(task);
      }
    }
    return matchingList;
  }
);

export const getGroupTaskListLength = createSelector(
  [(state: CommonRootState) => state, (_, groupId) => ({ groupId })],
  (state: CommonRootState, { groupId }) => {
    if (!groupId) return "";

    const groupTasksLength = tasksSelectByQueryOnlyLength(
      state.DBTasksReducer,
      {
        groupId: groupId,
      }
    );

    return groupTasksLength;
  }
);

export const columnListSelector = createSelector(
  [
    (state: CommonRootState, boardId) =>
      taskColumnSelectByQuery(
        state.DBTaskColumnReducer,
        {
          $or: [{ deleted: { $exists: false } }, { deleted: false }],
          boardId: boardId ?? "No Board", // if no board add a filter so no columns will be returned
        },
        [],
        {
          order: 1,
        }
      ),
    (state: CommonRootState, __, groupId?) => getGroupTaskListLength(state, groupId),
  ],
  (dynamicColumnList, groupTaskListLength) => {
    let columnList: TaskColumn[];

    columnList = [...staticColumnList, ...dynamicColumnList].sort(
      (colA, colB) => (colA.order ?? 0) - (colB.order ?? 0)
    );
    if (groupTaskListLength === 0) {
      columnList = columnList.splice(0, 2);
    }
    return columnList;
  }
);

export const columnTaskSelector = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState, columnId: NullableId, columnValue, isDefault) => ({
      columnId,
      columnValue,
      isDefault,
    }),
  ],
  (state: CommonRootState, { columnId, columnValue, isDefault }) => {
    const currentBoardTasks = currentTasks(state);
    const columnOptionsWithoutDefault = taskColumnOptionSelectByQuery(
      state.DBTaskColumnOptionReducer,
      {
        boardId: getSelectedBoardId(state),
        columnId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      },
      undefined,
      {
        order: 1,
      }
    )
      .filter((option) => option._id !== columnValue)
      .map((option) => option._id);

    let matchingList: Task[] = [];
    let unMatchingList: Task[] = [];
    for (let i = 0; i < currentBoardTasks.length; i++) {
      let isMatchingCustomValue = false;
      const task = currentBoardTasks[i];
      if (task.customData && columnId !== null && columnId !== undefined) {
        if (task.customData[columnId]) {
          if (task.customData[columnId].value === columnValue) {
            isMatchingCustomValue = true;
          }
        }
      }

      if (isMatchingCustomValue) {
        matchingList.push(task);
      } else if (isDefault) {
        if (task.customData && columnId !== null && columnId !== undefined) {
          if (task.customData[columnId] === undefined) {
            unMatchingList.push(task);
          } else if (
            columnOptionsWithoutDefault.indexOf(
              task.customData[columnId].value
            ) === -1
          ) {
            unMatchingList.push(task);
          }
        } else {
          unMatchingList.push(task);
        }
      }
    }

    const finalList = [...matchingList, ...unMatchingList].sort((a, b) => {
      if (columnId === null || columnId === undefined) return 0;
      const aOrder =
        a.customData === undefined ||
          a.customData[columnId] === undefined ||
          a.customData[columnId].order === undefined
          ? -1
          : a.customData[columnId].order;
      const bOrder =
        b.customData === undefined ||
          b.customData[columnId] === undefined ||
          b.customData[columnId].order === undefined
          ? -1
          : b.customData[columnId].order;
      return aOrder - bOrder;
    });

    return finalList;
  }
);

export const getKanbanColumnTasksByOrder = createSelector(
  [
    getSelectedBoardId,
    (state: CommonRootState) => state,
    (_, kanbanId) => kanbanId,
    (state: CommonRootState, __, columnId, columnValue, isDefault) =>
      columnTaskSelector(state, columnId, columnValue, isDefault),
  ],
  (boardId, state, kanbanId: NullableId, columnOptionTasks: Task[]) => {
    const kanban = kanbanSelectOneObjectByQuery(state.DBKanbanReducer, {
      boardId,
      _id: kanbanId,
    });
    let orderedTasksList: Task[] = [];
    const numberOfTasks = columnOptionTasks.length;
    if (kanban?.tasksOrder) {
      const tasksOrder = kanban.tasksOrder;
      orderedTasksList = columnOptionTasks.sort((a, b) => {
        const aId = a._id ?? '';
        const bId = b._id ?? '';
        let aOrder = numberOfTasks;
        if (tasksOrder[aId] !== undefined) {
          aOrder = tasksOrder[aId];
        }

        let bOrder = numberOfTasks;
        if (tasksOrder[bId] !== undefined) {
          bOrder = tasksOrder[bId];
        }

        return aOrder - bOrder;
      });
    } else {
      orderedTasksList = columnOptionTasks;
    }

    const returnList = orderedTasksList.map(
      (task, index) => ({ _id: task._id } as Task)
    );

    return returnList;
  }
);

export const selectColumnDetailsById = createSelector(
  [
    (state: CommonRootState, boardId, _) => columnListSelector(state, boardId),
    (_, boardId, columnId) => columnId,
  ],
  (columnList, columnId) => columnList.find((column) => column._id === columnId)
);
export const getWorkspaceUserById = createSelector(
  [getCurrentWorkspaceUserList, (state: CommonRootState, userId) => userId],
  (userList, userId) => {
    return userList.find((user) => user.userId === userId);
  }
);

export const usedComponentSelector = createSelector(
  [
    (state: CommonRootState, colType: ColumnType) => colType,
    (state: CommonRootState, colType) => state.TasksReducer.isScrollingStart,
    (state: CommonRootState, colType) => state.TasksReducer.isScrollingEnd,
    (state: CommonRootState, colType) => state.TasksReducer.currentFPS,
  ],
  (colType, isScrollingStart, isScrollingEnd, currentFPS) => {
    const columnType = columnTypeMap.get(colType)
    let usedComponent;
    if ((isScrollingStart || !isScrollingEnd) && (currentFPS ?? 0) < 30) {
      usedComponent = columnType?.scrollingCellComponent ?? columnType?.cellComponent;
    } else {
      usedComponent = columnType?.cellComponent;
    }

    return usedComponent;
  }
);

export const selectAudienceSelectedContactList = createSelector(
  [
    (state: CommonRootState) => state.DBTasksReducer,
    (state: CommonRootState) => state.DBTaskColumnReducer,
    (state: CommonRootState) => getSelectedBoardId(state),
  ],
  (DBTasksReducer, DBTaskColumnReducer, boardId) => {
    const boardTaskList = tasksSelectByQuery(DBTasksReducer, {
      boardId,
    });
    const currentBoardIdColumnList = taskColumnSelectByQuery(
      DBTaskColumnReducer,
      {
        deleted: false,
        boardId,
      }
    )
    const contactColumn = currentBoardIdColumnList.find((column) => column.type === "whatsapp-id-cell");
    const audienceSelectedContactList = boardTaskList.map((task) => {
      const colId = contactColumn?._id;
      return task.customData && colId !== undefined ? task.customData[colId]?.value as string : undefined;
    });

    return audienceSelectedContactList;
  }
);

export const selectAllContactsSelected = createSelector(
  [
    (state: CommonRootState) => state.WhatsAppReducer,
    (state: CommonRootState) => selectMergedContactList(state),
    (state: CommonRootState) => selectAudienceSelectedContactList(state),
  ],
  (WhatsAppReducer, mergedContactList: ContactInfo[], audienceSelectedContactList) => {
    const importedContactsArray = WhatsAppReducer.importedContactsArray;
    const selectedIdList = importedContactsArray.map((contact: { whatsappId?: string }) => contact.whatsappId);

    const allContacts = mergedContactList.map((contact) => contact.whatsappId);
    // Compare selectedIdList and audienceSelectedContactList with allContacts using Set
    const selectedContacts = new Set([...selectedIdList, ...audienceSelectedContactList]);
    const allContactsSet = new Set(allContacts);

    // Check if all selected contacts are in the audienceSelectedContacts
    const allContactsSelected = selectedContacts.size === allContactsSet.size && [...selectedContacts].every((contact) => allContactsSet.has(contact));

    return allContactsSelected;
  }
);


export const selectDoesContactExistInBoard = createSelector(
  [
    (state: CommonRootState) => selectAudienceSelectedContactList(state),
    (state: CommonRootState, whatsappId) => whatsappId
  ],
  (audienceSelectedContactList, whatsappId) => {
    const contactExistsInBoard = audienceSelectedContactList.some((contactId) => {
      return contactId === whatsappId;
    })

    return contactExistsInBoard;
  }
);